import type { CSSProperties } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { APP_BENEFITS, POWER_FEATURES } from '../../data/site';
import {
  APPLICATION_HERO,
  APPLICATION_INTRO,
  APPLICATION_PLATFORMS,
} from '../../data/application';
import { SiteButton } from '../site/SiteButton';
import { PlatformSection } from './PlatformSection';

const PLATFORM_CHIPS = ['MacBook', 'iPad Pro', 'iOS', 'Android'] as const;

export function ApplicationShowcase() {
  const { ref: introRef, visible: introVisible } = useScrollReveal<HTMLElement>(0.15);
  const { ref: stepsRef, visible: stepsVisible } = useScrollReveal<HTMLElement>(0.12);
  const { ref: ctaRef, visible: ctaVisible } = useScrollReveal<HTMLElement>(0.2);

  return (
    <div className="application-showcase">
      <section className="application-hero">
        <div className="container">
          <div className="application-hero__inner">
            <span className="application-hero__tag">{APPLICATION_HERO.tag}</span>
            <h1 className="application-hero__title">{APPLICATION_HERO.title}</h1>
            <p className="application-hero__subtitle paragraph-02 text-dark-grey-two">
              {APPLICATION_HERO.subtitle}
            </p>

            <div className="application-hero__chips">
              {PLATFORM_CHIPS.map((chip, i) => (
                <a key={chip} href={`#${APPLICATION_PLATFORMS[i].id}`} className="application-hero__chip">
                  {chip}
                </a>
              ))}
            </div>
          </div>

          <div className="application-hero__orbit" aria-hidden>
            {APPLICATION_PLATFORMS.map((p, i) => (
              <span
                key={p.id}
                className="application-hero__orbit-dot"
                style={{ '--orbit-i': i } as CSSProperties}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        ref={introRef}
        className={`application-intro${introVisible ? ' is-visible' : ''}`}
      >
        <div className="container">
          <div className="application-intro__grid">
            <div className="application-intro__copy reveal-item">
              <span className="tag">{APPLICATION_INTRO.tag}</span>
              <h2>{APPLICATION_INTRO.title}</h2>
              <p className="paragraph-02 text-dark-grey-two">{APPLICATION_INTRO.description}</p>
              <ul className="application-intro__benefits">
                {APP_BENEFITS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="application-intro__visual reveal-item">
              <img
                src={APPLICATION_INTRO.image}
                alt={APPLICATION_INTRO.imageAlt}
                className="application-intro__image"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {APPLICATION_PLATFORMS.map((platform, index) => (
        <PlatformSection key={platform.id} platform={platform} index={index} />
      ))}

      <section ref={stepsRef} className={`application-flow${stepsVisible ? ' is-visible' : ''}`}>
        <div className="container">
          <div className="application-flow__header reveal-item">
            <span className="tag">How it works</span>
            <h2>Same journey, every screen</h2>
            <p className="paragraph-02 text-dark-grey-two">
              Discover, connect, agree, and grow, whether you are at your desk or on the move.
            </p>
          </div>
          <div className="application-flow__steps">
            {POWER_FEATURES.map((item, index) => (
              <article key={item.title} className="application-flow__step reveal-item" style={{ transitionDelay: `${index * 0.08}s` }}>
                <span className="application-flow__step-num">0{index + 1}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section ref={ctaRef} className={`application-cta${ctaVisible ? ' is-visible' : ''}`}>
        <div className="container">
          <div className="application-cta__card reveal-item">
            <h2>Ready on Mac, iPad, and mobile</h2>
            <p className="paragraph-02 text-dark-grey-two">
              Join the waitlist and be first to experience Hello Gwiji across every platform.
            </p>
            <SiteButton href="/waitlist" variant="cta">
              Request early access
            </SiteButton>
          </div>
        </div>
      </section>
    </div>
  );
}
